import React, { createContext, useContext } from 'react';

interface PermissionAccessContextType {
  accessLevel: number;
}

const PermissionAccessContext = createContext<PermissionAccessContextType | undefined>(undefined);

export const PermissionAccessProvider: React.FC<{
  accessLevel: number;
  children: React.ReactNode;
}> = ({ accessLevel, children }) => (
  <PermissionAccessContext.Provider value={{ accessLevel }}>
    {children}
  </PermissionAccessContext.Provider>
);

export const useComponentAccessLevel = (): number | undefined => {
  const context = useContext(PermissionAccessContext);
  return context?.accessLevel;
};

export const useIsDisabledByPermission = (): boolean => {
  const accessLevel = useComponentAccessLevel();
  if (accessLevel === undefined) return false;
  return accessLevel <= 1;
};

/**
 * Drop-in replacement for <input> that auto-disables inside a PermissionsContainer
 * with View (1) access level. Use this for raw HTML inputs that aren't shared components.
 */
export const PermissionAwareInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const disabledByPermission = useIsDisabledByPermission();
  return <input ref={ref} {...props} disabled={props.disabled || disabledByPermission} />;
});
PermissionAwareInput.displayName = 'PermissionAwareInput';

/**
 * Drop-in replacement for <button> that auto-disables inside a PermissionsContainer
 * with View (1) access level. Use this for action buttons in permission-controlled sections.
 */
export const PermissionAwareButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, ...props }) => {
  const disabledByPermission = useIsDisabledByPermission();
  return <button {...props} disabled={props.disabled || disabledByPermission}>{children}</button>;
};

/**
 * Drop-in replacement for <select> that auto-disables inside a PermissionsContainer
 * with View (1) access level. Use this for raw HTML selects in permission-controlled sections.
 */
export const PermissionAwareSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ children, ...props }) => {
  const disabledByPermission = useIsDisabledByPermission();
  return <select {...props} disabled={props.disabled || disabledByPermission}>{children}</select>;
};

/**
 * Drop-in replacement for <textarea> that auto-disables inside a PermissionsContainer
 * with View (1) access level. Use this for raw HTML textareas in permission-controlled sections.
 */
export const PermissionAwareTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => {
  const disabledByPermission = useIsDisabledByPermission();
  return <textarea {...props} disabled={props.disabled || disabledByPermission} />;
};
